import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  GlobalStyles,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack, PictureAsPdf } from '@mui/icons-material';
import PageHeader from '@/components/PageHeader';
import { useI18n } from '@/i18n';
import { getClientBalance, getLocationBalance, type ClientBalanceReport } from '@/services/clientBalanceApi';

function normalizeStatus(status: string) {
  const normalized = status.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (normalized === 'available' || normalized === 'wolny') return 'available';
  if (normalized === 'occupied' || normalized === 'zajety') return 'occupied';
  if (normalized === 'damaged' || normalized === 'uszkodzony') return 'damaged';
  if (normalized === 'repair' || normalized === 'naprawa') return 'repair';
  if (normalized === 'cleaning' || normalized === 'mycie') return 'cleaning';
  if (normalized === 'blocked' || normalized === 'zablokowany') return 'blocked';
  return null;
}

function formatPostalCode(postalCode: string | null) {
  if (!postalCode) return '';
  const digits = postalCode.replace(/\D/g, '');
  return digits.length === 5 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : postalCode;
}

function StatCard({ label, value, note }: { label: string; value: string | number; note?: string }) {
  return (
    <Card sx={{ height: '100%', borderTop: '3px solid', borderTopColor: 'primary.main' }}>
      <CardContent>
        <Stack spacing={0.75}>
          <Typography variant="overline" color="text.secondary">{label}</Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.dark' }}>{value}</Typography>
          {note ? <Typography variant="caption" color="text.secondary">{note}</Typography> : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ClientBalancePage() {
  const { clientId = '', locationId = '' } = useParams();
  const navigate = useNavigate();
  const { t, language } = useI18n();
  const [report, setReport] = useState<ClientBalanceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    (locationId ? getLocationBalance(locationId) : getClientBalance(clientId))
      .then((data) => {
        if (!cancelled) setReport(data);
      })
      .catch(() => {
        if (!cancelled) setError(t('clientBalance.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [clientId, locationId, t]);

  useEffect(() => {
    if (!report) return;
    const previousTitle = document.title;
    document.title = `${t('clientBalance.title')} - ${report.scope_name}`;
    return () => { document.title = previousTitle; };
  }, [report, t]);

  const formatDate = (value: string | null, withTime = false) => {
    if (!value) return t('clientBalance.noHistory');
    return new Intl.DateTimeFormat(language === 'pl' ? 'pl-PL' : 'en-GB', withTime
      ? { dateStyle: 'medium', timeStyle: 'short' }
      : { dateStyle: 'medium' }).format(new Date(value));
  };

  const printStyles = {
    '@media print': {
      '@page': { size: 'A4 portrait', margin: '8mm' },
      'body *': { visibility: 'hidden' },
      '#client-balance-print, #client-balance-print *': { visibility: 'visible' },
      '#client-balance-print': {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        padding: '0 !important',
        backgroundColor: '#fff',
        color: '#111827',
        fontSize: '8pt',
      },
      '.client-balance-no-print': { display: 'none !important' },
      '.MuiPaper-root': { boxShadow: 'none !important', borderColor: '#d9e0e8 !important' },
      '#client-balance-print .MuiTypography-root': { fontSize: '8pt', lineHeight: 1.25, color: '#111827' },
      '#client-balance-print .MuiTypography-h5': { fontSize: '13pt', lineHeight: 1.2, fontWeight: 800 },
      '#client-balance-print .MuiTypography-h6': { fontSize: '9pt', lineHeight: 1.2, fontWeight: 800 },
      '#client-balance-print .MuiTypography-h3': { fontSize: '15pt', lineHeight: 1.1, fontWeight: 800 },
      '#client-balance-print .MuiTypography-overline': { fontSize: '6.5pt', lineHeight: 1.1 },
      '#client-balance-print .MuiStack-root': { gap: '5px !important' },
      '#client-balance-print .MuiGrid-container': { marginTop: '0 !important' },
      '#client-balance-print .MuiGrid-item': { paddingTop: '4px !important', paddingLeft: '4px !important' },
      '#client-balance-print .MuiCardContent-root': { padding: '7px !important' },
      '#client-balance-print .MuiPaper-root': { borderRadius: '3px !important' },
      '#client-balance-print .MuiAlert-root': { padding: '6px 9px !important', border: '1px solid #cbd5e1', background: '#f8fafc !important', color: '#111827 !important' },
      '#client-balance-print .MuiAlert-icon': { display: 'none' },
      '#client-balance-print .MuiTableCell-root': { padding: '3px 5px !important', fontSize: '7pt', lineHeight: 1.15, color: '#111827' },
      '#client-balance-print .MuiTableCell-head': { fontWeight: 800, background: '#e8eef5 !important' },
      '#client-balance-print .MuiChip-root': { height: '17px', fontSize: '7pt', color: '#111827 !important', borderColor: '#94a3b8 !important' },
      '#client-balance-print table': { width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' },
      '#client-balance-print thead': { display: 'table-header-group' },
      '#client-balance-print tr': { breakInside: 'avoid', pageBreakInside: 'avoid' },
      '#client-balance-print .MuiTableContainer-root': { overflow: 'visible !important' },
      '#client-balance-print .MuiTable-root': { minWidth: '0 !important' },
    },
  };

  return (
    <>
      <GlobalStyles styles={printStyles} />
      <Box id="client-balance-print">
        <Stack spacing={3}>
          <Box className="client-balance-no-print">
            <PageHeader
              eyebrow={t('clientBalance.eyebrow')}
              title={report?.scope_name ?? t('clientBalance.title')}
              description={t('clientBalance.description')}
              action={(
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/assets')}>
                    {t('clientBalance.backToAssets')}
                  </Button>
                  <Button variant="contained" startIcon={<PictureAsPdf />} onClick={() => window.print()} disabled={!report || loading}>
                    {t('clientBalance.exportPdf')}
                  </Button>
                </Stack>
              )}
            />
          </Box>

          {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}

          {report && !loading ? (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{t('clientBalance.title')}: {report.scope_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('clientBalance.generatedAt')}: {formatDate(report.generated_at, true)}
                </Typography>
              </Stack>

              <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="overline" color="text.secondary">{t('clientBalance.recipient')}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{report.company_name || report.scope_name}</Typography>
                    {report.address_line ? <Typography>{report.address_line}</Typography> : null}
                    <Typography>
                      {[formatPostalCode(report.postal_code), report.city].filter(Boolean).join(' ')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="overline" color="text.secondary">{t('clientBalance.statementLabel')}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{t('clientBalance.statementTitle')}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('clientBalance.reference')}: {report.scope_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('clientBalance.statementDate')}: {formatDate(report.generated_at)}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Alert severity="warning" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>{t('clientBalance.returnRequestTitle')}</Typography>
                <Typography variant="body2">{t('clientBalance.returnRequest')}</Typography>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <StatCard label={t('clientBalance.currentStock')} value={report.current_stock_count} note={t('clientBalance.variboxUnit')} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatCard label={t('clientBalance.recordedDays')} value={report.recorded_client_days} note={t('clientBalance.recordedDaysNote')} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <StatCard label={t('clientBalance.missingHistory')} value={report.variboxes_without_start_date} note={t('clientBalance.missingHistoryNote')} />
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('clientBalance.detailsTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('clientBalance.detailsDescription')}</Typography>
                </Box>
                {report.variboxes.length ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('clientBalance.serialNumber')}</TableCell>
                          <TableCell>RADEX</TableCell>
                          <TableCell>{t('clientBalance.status')}</TableCell>
                          <TableCell>{t('clientBalance.location')}</TableCell>
                          <TableCell>{t('clientBalance.since')}</TableCell>
                          <TableCell align="right">{t('clientBalance.days')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.variboxes.map((item) => (
                          <TableRow key={item.varibox_id}>
                            <TableCell sx={{ fontWeight: 700 }}>{item.serial_number}</TableCell>
                            <TableCell>{item.radex_identifier}</TableCell>
                            <TableCell>
                              {(() => {
                                const statusKey = normalizeStatus(item.status);
                                if (!statusKey) return item.status;
                                const color = statusKey === 'available' ? 'success'
                                  : statusKey === 'occupied' ? 'info'
                                    : statusKey === 'repair' || statusKey === 'cleaning' ? 'warning'
                                      : statusKey === 'blocked' || statusKey === 'damaged' ? 'error' : 'default';
                                return <Chip size="small" color={color} variant="outlined" label={t(`assets.statuses.${statusKey}`)} />;
                              })()}
                            </TableCell>
                            <TableCell>{item.location}</TableCell>
                            <TableCell>{formatDate(item.since)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>
                              {item.days_at_client === null ? t('clientBalance.noHistory') : item.days_at_client}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ p: 3 }}><Typography color="text.secondary">{t('clientBalance.empty')}</Typography></Box>
                )}
              </Paper>

              <Typography variant="caption" color="text.secondary">
                {t('clientBalance.calculationNote')}
              </Typography>
            </>
          ) : null}
        </Stack>
      </Box>
    </>
  );
}
